import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import {
	AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD,
	AI_MENU_SECTION,
	AI_MENU_SECTION_RANK,
	AI_REMIX_MENU_ITEM,
} from '@atlaskit/editor-common/block-menu';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RegisterBlockMenuComponent } from '@atlaskit/editor-plugin-block-menu';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { MalleableUiPlugin } from '../../malleableUiPluginType';

const RemixBlockMenuItem = ({ api }: { api: ExtractInjectionAPI<MalleableUiPlugin> }) => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();

	// Fire experiment exposure on mount — registration uses expValEqualsNoExposure
	// to avoid over-counting on every editor load. The exposure is recorded here
	// when the Remix button is actually visible to the user.
	useEffect(() => {
		expValEquals('platform_editor_remix_in_block_menu', 'isEnabled', true);
	}, []);

	const handleClick = useCallback(() => {
		// Close block menu and clear selection highlight
		api?.core?.actions.execute(({ tr }) => {
			api?.blockControls?.commands?.stopPreservingSelection()({ tr });
			api?.blockControls?.commands?.toggleBlockMenu({ closeMenu: true })({ tr });
			api?.selection?.commands?.clearBlockSelection()({ tr });
			return tr;
		});

		// Fire analytics
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_REMIX_BUTTON,
			eventType: EVENT_TYPE.UI,
			attributes: {
				entryPoint: 'editor',
				triggeredFrom: 'blockMenu',
			},
		});

		// Start experience tracking and open remix modal.
		// malleableUi is guaranteed to exist here — this component is always
		// registered by malleableUiPlugin itself.
		const openRemixModal = api.malleableUi?.commands.openRemixModal({
			metadata: { interactionSource: 'remixModalFromBlockMenu' },
			source: 'remixModalFromBlockMenu',
		});

		if (openRemixModal) {
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'blockMenu',
				},
			});
			api.core.actions.execute(openRemixModal);
		}
	}, [api, experienceTracker]);

	return (
		<ToolbarDropdownItem
			elemBefore={<AiGenerativeRemixIcon label="" size="small" />}
			onClick={handleClick}
		>
			{formatMessage(aiConfigItemMessages.remixTitle)}
		</ToolbarDropdownItem>
	);
};

/**
 * Creates block menu registration components for the Remix action.
 *
 * Registered by editor-plugin-malleable-ui (which owns the openRemixModal command)
 * when the platform_editor_remix_in_block_menu experiment is enabled.
 * The AI plugin hides its polish/improve-formatting action when this experiment is on,
 * so the Remix item effectively replaces it.
 */
const createRemixBlockMenuItemComponent = (api: ExtractInjectionAPI<MalleableUiPlugin>) => {
	const RemixBlockMenuItemWrapper = () => <RemixBlockMenuItem api={api} />;
	RemixBlockMenuItemWrapper.displayName = 'RemixBlockMenuItemWrapper';
	return RemixBlockMenuItemWrapper;
};

export const getRemixBlockMenuComponents = ({
	api,
	getEditorView,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	getEditorView: () => EditorView | undefined;
}): RegisterBlockMenuComponent[] => {
	return [
		{
			type: 'block-menu-item' as const,
			key: `ai-${AI_REMIX_MENU_ITEM.key}`,
			parent: {
				type: 'block-menu-section' as const,
				key: AI_MENU_SECTION.key,
				rank: (AI_MENU_SECTION_RANK as Record<string, number>)[AI_MENU_SECTION.key],
			},
			component: createRemixBlockMenuItemComponent(api),
			isHidden: () => {
				if (!expValEqualsNoExposure('platform_editor_ai_blockmenu_integration', 'isEnabled', true)) {
					return true;
				}
				const editorView = getEditorView();
				const textLength = editorView
					? api?.blockControls?.actions.getTextLength(editorView) ?? null
					: null;
				// When context is unavailable, default to visible (matches AI plugin behaviour)
				if (textLength === null) {
					return false;
				}
				return textLength < AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD;
			},
		},
	];
};
